import {gql} from '@apollo/client';
import {captureException} from '@sentry/browser';
import {css, StyleSheet} from 'aphrodite';
import React, {createContext, ErrorInfo, ReactNode, useMemo} from 'react';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {Chat} from 'llm/chat-library/Chat';
import {MCPToolMappingProvider} from 'llm/mcp-client-library/MCPToolMappingContext';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {assertNever} from 'util/Types';

import {ChatTypeQuery, ChatTypeQueryVariables} from './__generated/ChatTypeQuery';

/**
 * A chat request is sent to the ChatRouter to render the appropriate chat component.
 *
 * This supports a few cases:
 *
 * |  id    |   type   |  intention                                                                   |
 * | ------ | -------- | ---------------------------------------------------------------------------- |
 * | string |  null    | An existing chat whose type is not known. A query will resolve the type.     |
 * | string | ChatType | An existing chat whose type is known. Renders immediately.                   |
 *
 */
export interface ExistingChatRequest {
  id: string;
  type: ChatType | null;
}

export interface NewChatRequest {
  id: null;
  type: ChatType | null;
  agentType?: AgentType;
}

export type ChatRequest = ExistingChatRequest | NewChatRequest;

export const CHAT_TYPE_QUERY = gql`
  query ChatTypeQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    chatSession(id: $id) {
      ... on ChatSessionNode {
        id
        type
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Given a ChatRequest, determine the chat type, querying the backend if necessary.
 *
 * A query is needed if the id is specified *without* the type. See {@link ChatRequest} for more details.
 */
function useResolvedType(
  chat: ExistingChatRequest
): {status: 'RESOLVED'; value: ChatType | null} | {status: 'INACCESSIBLE' | 'PENDING' | 'ERROR'} {
  const result = useSkippableBenchlingQuery<ChatTypeQuery, ChatTypeQueryVariables>(CHAT_TYPE_QUERY, {
    variables: {id: chat.id},
    fetchPolicy: 'cache-first',
    skip: chat.type != null,
  });
  if (chat.type != null) {
    return {status: 'RESOLVED', value: chat.type};
  }
  switch (result.type) {
    case QueryResultType.SUCCESS:
      return !isInaccessible(result.data.chatSession)
        ? {status: 'RESOLVED', value: result.data.chatSession.type}
        : {status: 'INACCESSIBLE'};
    case QueryResultType.ERROR:
      return {status: 'ERROR'};
    case QueryResultType.PENDING:
      return {status: 'PENDING'};
    case QueryResultType.SKIPPED:
      throw new Error(`Unexpected query status: ${result.type}`);
    default:
      throw new Error(`Unexpected query status: ${assertNever(result).type}`);
  }
}

/**
 * Given a chat whose type is known, route to the appropriate chat component.
 */
function ResolvedTypeChat({
  chat,
  isExpanded,
}: {
  chat: ExistingChatRequest & {type: ChatType};
  isExpanded: boolean;
}): JSX.Element {
  const contextValue = useMemo(() => ({chatId: chat.id, chatType: chat.type}), [chat.id, chat.type]);

  return (
    <MCPToolMappingProvider>
      <ChatContext.Provider value={contextValue}>
        <Chat chatSessionId={chat.id} chatType={chat.type} isExpanded={isExpanded} />
      </ChatContext.Provider>
    </MCPToolMappingProvider>
  );
}

/**
 * A chat container that takes up the full width of the expanded sidepanel with a reasonable max width.
 *
 * TODO: This should be pulled out into a shared component.
 */
function FullWidthChatContainer({children}: {children?: ReactNode}): JSX.Element {
  return <div className={css(styles.fullWidthChatContainer)}>{children}</div>;
}

/**
 * Renders an error message in place of the chat component
 */
function ErrorChat({message}: {message: string}): JSX.Element {
  return (
    <FullWidthChatContainer>
      <InlineBanner text={message} bannerType={InlineBannerType.ERROR} />
    </FullWidthChatContainer>
  );
}

/**
 * Ideally, this would be a new chat where the user hasn't specified the type yet. We don't support
 * this yet, so for now, we render a blank page. The user can still interact with the rest of the sidepanel.
 */
function EmptyNewChat(): JSX.Element {
  return <FullWidthChatContainer />;
}

/**
 * Render a loading indicator in place of the chat component.
 */
function LoadingChat(): JSX.Element {
  return (
    <div className={css(styles.loadingContainer)}>
      <CenteredHorizontalThrobber />
    </div>
  );
}

interface ChatRouterProps {
  chat: ExistingChatRequest;
  isExpanded: boolean;
}

interface ChatRouterState {
  hasError: boolean;
}

export const ChatContext = createContext<{chatId: string | null; chatType: ChatType} | null>(null);

/**
 * Main rendering for {@link ChatRouter}, factored out into its own component to add an error boundary.
 */
function InnerChatRouter({chat, isExpanded}: ChatRouterProps): JSX.Element | null {
  const result = useResolvedType(chat);
  switch (result.status) {
    case 'RESOLVED':
      return result.value != null ? (
        <ResolvedTypeChat chat={{id: chat.id, type: result.value}} isExpanded={isExpanded} />
      ) : (
        <EmptyNewChat />
      );
    case 'INACCESSIBLE':
      return <ErrorChat message="You do not have access to this chat." />;
    case 'PENDING':
      return <LoadingChat />;
    case 'ERROR':
      return <ErrorChat message="An error occurred loading the chat." />;
    default:
      throw new Error(`Unexpected result: ${assertNever(result).type}`);
  }
}

/**
 * Routes a {@link ChatRequest} to the appropriate chat component.
 * Includes an error boundary to render an inline error and report to Sentry.
 *
 * The transition of {@link ChatRequest.id} from `null` to `string` is a signal that the chat has been
 * persisted. The relevant chat component will re-render (without unmounting) with the new id.
 *
 * In the case one chat is being replaced by a different chat, it is recommended that the ChatRouter
 * receive a new key, forcing it to unmount and re-mount.
 */
export default class ChatRouter extends React.Component<ChatRouterProps, ChatRouterState> {
  override state: ChatRouterState = {hasError: false};

  static getDerivedStateFromError(error: Error): ChatRouterState {
    return {hasError: true};
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureException(error, {extra: {errorInfo}});
  }

  override render(): ReactNode {
    return this.state.hasError ? (
      <ErrorChat message="An error occurred displaying the chat. Try refreshing the page." />
    ) : (
      <InnerChatRouter {...this.props} />
    );
  }
}

const styles = StyleSheet.create({
  fullWidthChatContainer: {
    width: '100%',
    margin: '0 auto',
    maxWidth: '800px',
    padding: '16px',
  },
  loadingContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
});
